import { ATTR, MODE_STRONG, PORTRAIT_BY_HOLDER } from "../constants";
import { asBufferSource } from "../crypto";
import { escapeHTML, formatWhen, REASON_COPY, type RefusalReason } from "../ui";
import type { ErrandView, VerifyResult } from "../verify";
import { ICON } from "../icons";

/**
 * A verification result, or a refusal the screen layer produced on its own.
 *
 * `renderScan` reaches here with `device-error` when the phone fails
 * underneath the verifier — a rejected IndexedDB write, most of all — and a
 * verifier who scanned something has to be told what happened either way.
 */
export type ResultView = Omit<VerifyResult, "reason"> & { reason: RefusalReason };

/**
 * How old the code is, in the verifier's words.
 *
 * `verifyPresentation` clamps the age at zero, because the holder's phone may
 * be running ahead of this one and a negative number is not something the
 * verification layer should be inventing display policy about. That leaves
 * zero meaning two different things: a code generated this second, and a code
 * generated up to a minute ago by a phone whose clock is ahead.
 *
 * Both are inside the freshness window — anything outside it was already
 * refused — so the verifier's decision is the same either way, and the only
 * thing a literal `hace 0 s` adds is a number that looks more precise than it
 * is. The words say what is actually known: this code is fresh.
 */
function ageCopy(ageSeconds: number): string {
  return ageSeconds === 0
    ? "Código recién generado"
    : `Código generado hace ${ageSeconds} s`;
}

/**
 * In place of the age on a strong-mode acceptance. The answer carries the
 * verifier's own nonce, so what makes it fresh is the challenge this phone
 * issued, and the holder's timestamp was not what was checked.
 */
export const STRONG_COPY = "Modo reforzado: el código responde a tu desafío";

const plural = (count: number, one: string, many: string) =>
  `${count} ${count === 1 ? one : many}`;

/**
 * What the status list said, in the verifier's words. Always shown on an
 * accepted credential, because acceptance means something different with a
 * list from this morning, with a list from last month, and with no list: the
 * verifier is told which rather than left to assume the best one.
 */
export function statusCopy(state: VerifyResult["statusState"], ageSeconds?: number): string {
  if (state === "beyond") return "Credencial más nueva que la lista de estado";
  if (state !== "valid" || ageSeconds === undefined) {
    return "Sin lista de estado: no se puede comprobar si fue revocada";
  }
  if (ageSeconds < 3600) return "Lista de estado de hace menos de una hora";
  if (ageSeconds < 86_400) {
    return `Lista de estado de hace ${plural(Math.floor(ageSeconds / 3600), "hora", "horas")}`;
  }
  return `Lista de estado de hace ${plural(Math.floor(ageSeconds / 86_400), "día", "días")}`;
}

/**
 * The authorisation under the bearer's identity, in the spec's words:
 * «Autorizado por <nombre>, <cargo>: <texto>. Vence <fecha>». Its own block,
 * below the identity, because the face on screen is the bearer's and the name
 * here is someone who is not in the room.
 */
export function errandHTML(errand: ErrandView): string {
  return `<section class="errand-block">
      <h2>Recado</h2>
      <p>Autorizado por <strong>${escapeHTML(errand.grantorName)}</strong>,
      ${escapeHTML(errand.grantorRole)}: ${escapeHTML(errand.text.replace(/[.\s]+$/, ""))}.</p>
      <p>Vence ${escapeHTML(formatWhen(errand.expiresAt))}</p>
    </section>`;
}

/**
 * `onAgain` rather than `location.reload()`: a reload boots straight back to
 * the card, so the button labelled `Escanear otra vez` would take a verifier
 * everywhere except the scanner.
 */
export function renderResult(
  root: HTMLElement,
  result: ResultView,
  onAgain: () => void,
): void {
  if (!result.ok || !result.attributes) {
    root.innerHTML = `<div class="result bad"><div class="badge">${ICON.cross}</div>
      <h1>No verificada</h1><p>${escapeHTML(REASON_COPY[result.reason])}</p>
      <div class="foot"><button id="again">Escanear otra vez</button></div></div>`;
  } else {
    const portrait = result.attributes.get(ATTR.PORTRAIT) as Uint8Array | undefined;
    const portraitURL = portrait
      ? URL.createObjectURL(new Blob([asBufferSource(portrait)], { type: "image/webp" }))
      : null;
    root.innerHTML = `<div class="result good"><div class="badge">${ICON.check}</div>
      <div class="verdict">Identidad verificada</div>
      ${
        portraitURL
          ? `<img class="portrait" src="${portraitURL}" alt="">`
          : `<div class="portrait none">${ICON.person}</div>
             <p class="warn">Retrato no verificado: la UH no tiene foto de esta persona.
             Comprueba su identidad por otro medio.</p>`
      }
      ${portraitURL && result.attributes.get(ATTR.PORTRAIT_ORIGIN) === PORTRAIT_BY_HOLDER
        ? `<p class="warn origin">Foto puesta por la persona; la UH no la ha comprobado.</p>`
        : ""}
      <h1>${escapeHTML(result.attributes.get(ATTR.NAME))}</h1>
      <p class="role">${escapeHTML(result.attributes.get(ATTR.ROLE))}</p>
      <p class="dep">${escapeHTML(result.attributes.get(ATTR.DEPARTMENT))} · ${escapeHTML(result.attributes.get(ATTR.FACULTY))}</p>
      ${result.mode === MODE_STRONG
        ? `<p class="mode">${escapeHTML(STRONG_COPY)}</p>`
        : `<p class="age">${escapeHTML(ageCopy(result.ageSeconds ?? 0))}</p>`}
      <p class="status">${escapeHTML(statusCopy(result.statusState, result.statusAgeSeconds))}</p>
      ${result.errand ? errandHTML(result.errand) : ""}
      <div class="foot"><button id="again">Escanear otra vez</button></div></div>`;
  }
  root.querySelector<HTMLButtonElement>("#again")!.addEventListener("click", onAgain);
}
